import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notFound } from "../../../../Constant";
import { leaveTemplate } from "../../../../Constant/HR/leaveTemplate";
import BaseInput from "../../../../BaseComponents/BaseInput";
import BackButton from "../../../../BaseComponents/BackButton";
import BaseButton from "../../../../BaseComponents/BaseButton";
import BaseDatePicker from "../../../../BaseComponents/BaseDatePicker";
import { leaveTitle } from "../../../../Constant/title";
import { validationMessages } from "../../../../Constant/validation";
import { useFormik } from "formik";
import * as yup from "yup";
import { checkStatusCodeSuccess, isRequired } from "../../../../Constant/common";
import { toast } from "react-toastify";
import BaseModal from "../../../../BaseComponents/BaseModal";
import {
  addLeaveTemplateApi,
  deleteLeaveCategoryApi,
  editLeaveTemplateApi,
  viewLeaveTemplateApi,
} from "../../../../Api/leaveTemplate";
import { useSelectedCenter } from "../../../CenterContext";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import { PlaceHolderFormat } from "../../../../Constant/requireMessage";
import { Plus, Trash2, GitBranch, AlertCircle } from "react-feather";

const AddEditLeaveTemplate = ({ isOnboarding = false, onSuccess }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [leaveIndexToDelete, setLeaveIndexToDelete] = useState(null);
  const [dltBtnLoader, setDltBtnLoader] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [isYearly, setIsYearly] = useState(false);
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();
  const leaveFormRef = useRef(null);

  useEffect(() => {
    if (!isOnboarding) {
      document.title = leaveTitle;
    }
  }, [isOnboarding]);

  const baseLeaveSchema = {
    leave_template_name: yup
      .string()
      .required(validationMessages.required(leaveTemplate.TEMPLATE_NAME)),
    leave_policy_cycle: yup
      .string()
      .required(validationMessages.required(leaveTemplate.LEAVE_POLICY_CYCLE)),
    leaves: yup.array().of(
      yup.object({
        leave_category_name: yup
          .string()
          .required(validationMessages.required(leaveTemplate.LEAVE_CATEGORY_NAME)),
        leave_count: yup
          .number()
          .typeError("Must be a number")
          .required(validationMessages.required(leaveTemplate.LEAVE_COUNT)),
        carry_forward_limit: yup
          .number()
          .typeError("Must be a number")
          .required(validationMessages.required(leaveTemplate.CARRY_LIMIT)),
      }),
    ),
  };

  const yearlySchema = yup.object({
    ...baseLeaveSchema,
    leave_start_date: yup
      .string()
      .test("start", validationMessages.required(leaveTemplate.FROM_DATE), function (v) {
        return this.parent.leave_policy_cycle === leaveTemplate.YEARLY ? !!v : true;
      }),
    leave_end_date: yup
      .string()
      .test("end", validationMessages.required(leaveTemplate.TO_DATE), function (v) {
        return this.parent.leave_policy_cycle === leaveTemplate.YEARLY ? !!v : true;
      }),
  });

  const calculateEndDate = useCallback((startDate, cycle) => {
    const date = new Date(startDate);
    if (cycle === leaveTemplate.MONTHLY) date.setMonth(date.getMonth() + 1);
    else if (cycle === leaveTemplate.YEARLY) date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split("T")[0];
  }, []);

  const leaveForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      leave_template_name: null,
      leave_start_date: null,
      leave_end_date: null,
      leave_policy_cycle: leaveTemplate.MONTHLY,
      leaves: [{ leave_category_name: null, leave_count: null, carry_forward_limit: null }],
    },
    validationSchema: isYearly ? yearlySchema : yup.object(baseLeaveSchema),
    onSubmit: (values) => {
      const payload = {
        ...selectedCenterPayload,
        leave_template_name: values.leave_template_name,
        leave_policy_cycle: values.leave_policy_cycle,
        leaves: values.leaves?.map((l) => ({
          ...l,
          carry_forward_limit: l?.carry_forward_limit ? Number(l.carry_forward_limit) : 0,
          leave_count: l?.leave_count ? Number(l.leave_count) : 0,
        })),
      };
      if (values.leave_policy_cycle === leaveTemplate.YEARLY) {
        payload.leave_start_date = values.leave_start_date;
        payload.leave_end_date = values.leave_end_date;
      }
      setBtnLoader(true);
      const apiCall = id ? editLeaveTemplateApi(id, payload) : addLeaveTemplateApi(payload);
      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            leaveForm.resetForm();
            if (isOnboarding && onSuccess) {
              onSuccess(resp?.data?.id ?? resp?.data);
            } else {
              navigate("/leaveTemplate");
            }
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message ?? err?.message))
        .finally(() => setBtnLoader(false));
    },
  });

  useEffect(() => {
    leaveFormRef.current = leaveForm;
  }, [leaveForm]);

  const leaveStartDate = leaveForm.values.leave_start_date;
  const leavePolicyCycle = leaveForm.values.leave_policy_cycle;
  const setLeaveEndDate = leaveForm.setFieldValue;
  useEffect(() => {
    if (leaveStartDate && leavePolicyCycle)
      setLeaveEndDate("leave_end_date", calculateEndDate(leaveStartDate, leavePolicyCycle));
  }, [leaveStartDate, leavePolicyCycle, calculateEndDate, setLeaveEndDate]);

  const fetchData = useCallback(
    (fetchId) => {
      if (!fetchId) return;
      setLoading(true);
      viewLeaveTemplateApi(fetchId)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            const d = resp?.data;
            leaveFormRef.current?.setValues({
              leave_template_name: d?.leave_template_name,
              leave_policy_cycle: d?.leave_policy_cycle,
              leave_start_date: d?.leave_start_date,
              leave_end_date: d?.leave_end_date,
              leaves: d?.LeaveCategory,
            });
            setIsYearly(d?.leave_policy_cycle === leaveTemplate.YEARLY);
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message ?? err?.message))
        .finally(() => setLoading(false));
    },
    [setLoading],
  );

  useEffect(() => {
    fetchData(id);
  }, [id, fetchData]);

  const confirmDelete = () => {
    const updatedLeaves = [...leaveForm.values.leaves];
    setDltBtnLoader(true);
    const toDelete = updatedLeaves[leaveIndexToDelete];
    if (toDelete.id) {
      deleteLeaveCategoryApi(toDelete.id, { is_deleted: true })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            fetchData(id);
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message ?? err?.message))
        .finally(() => {
          setIsModalOpen(false);
          setDltBtnLoader(false);
        });
    } else {
      updatedLeaves.splice(leaveIndexToDelete, 1);
      leaveForm.setValues({ ...leaveForm.values, leaves: updatedLeaves });
      setIsModalOpen(false);
      setDltBtnLoader(false);
    }
  };

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      {!isOnboarding && (
        <div
          className="animate-slide-down"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {id ? leaveTemplate.EDIT_TEMPLATE : leaveTemplate.CREATE_TEMPLATE}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {id
                ? "Update leave categories and policy settings"
                : "Define leave categories and policy for your staff"}
            </p>
          </div>
          <BackButton onClick={() => navigate("/leaveTemplate")} />
        </div>
      )}

      <form
        onSubmit={leaveForm.handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        {/* Template settings */}
        <div
          className="animate-slide-up"
          style={{
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 20,
          }}
        >
          <div
            style={{
              height: 3,
              background: "linear-gradient(90deg,#f43f5e,#f97316)",
              borderRadius: "20px 20px 0 0",
            }}
          />
          <div style={{ padding: "20px 24px" }}>
            <h2
              style={{
                margin: "0 0 16px",
                fontSize: "0.9375rem",
                fontWeight: 700,
                color: "var(--text-primary)",
              }}
            >
              {leaveTemplate.TEMPLATE_SETTING}
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                gap: 16,
              }}
            >
              <BaseInput
                name="leave_template_name"
                type="text"
                label={leaveTemplate.TEMPLATE_NAME}
                placeholder={PlaceHolderFormat(leaveTemplate.TEMPLATE_NAME)}
                handleChange={leaveForm.handleChange}
                value={leaveForm.values.leave_template_name}
                touched={leaveForm.touched.leave_template_name}
                error={leaveForm.errors.leave_template_name}
                handleBlur={leaveForm.handleBlur}
                required
              />

              {/* Policy cycle toggle */}
              <div>
                <label
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                    display: "block",
                    marginBottom: 8,
                  }}
                >
                  {leaveTemplate.LEAVE_POLICY_CYCLE} {isRequired}
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[leaveTemplate.MONTHLY, leaveTemplate.YEARLY].map((cycle) => {
                    const active = leaveForm.values.leave_policy_cycle === cycle;
                    return (
                      <button
                        key={cycle}
                        type="button"
                        onClick={() => {
                          leaveForm.setFieldValue("leave_policy_cycle", cycle);
                          setIsYearly(cycle === leaveTemplate.YEARLY);
                        }}
                        style={{
                          padding: "7px 18px",
                          borderRadius: 9,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          border: `1.5px solid ${active ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                          background: active ? "rgba(99,102,241,0.1)" : "var(--icon-btn-bg)",
                          color: active ? "var(--brand-indigo)" : "var(--text-secondary)",
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {cycle}
                      </button>
                    );
                  })}
                </div>
              </div>

              {isYearly && (
                <>
                  <BaseDatePicker
                    name="leave_start_date"
                    label={leaveTemplate.FROM_DATE}
                    placeholder={PlaceHolderFormat(leaveTemplate.FROM_DATE)}
                    onChange={(v) => leaveForm.setFieldValue("leave_start_date", v)}
                    value={leaveForm.values.leave_start_date}
                    touched={leaveForm.touched.leave_start_date}
                    error={leaveForm.errors.leave_start_date}
                    onBlur={leaveForm.handleBlur}
                    required
                  />
                  <BaseDatePicker
                    name="leave_end_date"
                    label={leaveTemplate.TO_DATE}
                    placeholder={PlaceHolderFormat(leaveTemplate.TO_DATE)}
                    onChange={(v) => leaveForm.setFieldValue("leave_end_date", v)}
                    value={leaveForm.values.leave_end_date}
                    touched={leaveForm.touched.leave_end_date}
                    error={leaveForm.errors.leave_end_date}
                    onBlur={leaveForm.handleBlur}
                    disabled
                    required
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Leave categories */}
        <div
          className="animate-slide-up"
          style={{
            animationDelay: "60ms",
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 20,
          }}
        >
          <div
            style={{
              height: 3,
              background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
              borderRadius: "20px 20px 0 0",
            }}
          />
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid var(--glass-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: "rgba(99,102,241,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GitBranch size={15} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                {leaveTemplate.LEAVE_CATEGORIES}
                <span
                  style={{
                    marginLeft: 8,
                    padding: "2px 8px",
                    borderRadius: 999,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    background: "rgba(99,102,241,0.1)",
                    color: "var(--brand-indigo)",
                  }}
                >
                  {leaveForm.values.leaves?.length}
                </span>
              </h2>
            </div>
            <button
              type="button"
              onClick={() =>
                leaveForm.setFieldValue("leaves", [
                  ...leaveForm.values.leaves,
                  {
                    leave_category_name: null,
                    leave_count: null,
                    carry_forward_limit: null,
                    isNew: true,
                  },
                ])
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 14px",
                borderRadius: 9,
                background: "var(--brand-indigo)",
                border: "none",
                color: "#fff",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <Plus size={14} strokeWidth={2.5} /> {leaveTemplate.ADD_LEAVE_CATEGORY}
            </button>
          </div>

          {leaveForm.values.leaves?.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "48px 20px",
                gap: 10,
              }}
            >
              <AlertCircle size={36} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
              <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {leaveForm.values.leaves.map((leave, index) => {
                const isOther = leave.leave_category_name === leaveTemplate.OTHER_LEAVE;
                return (
                  <div
                    key={leave.id ?? `new-${index}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr auto",
                      gap: 16,
                      padding: "14px 24px",
                      alignItems: "start",
                      borderBottom:
                        index < leaveForm.values.leaves.length - 1
                          ? "1px solid var(--glass-border)"
                          : "none",
                    }}
                  >
                    <BaseInput
                      type="text"
                      label={leaveTemplate.LEAVE_CATEGORY_NAME}
                      name={`leaves.${index}.leave_category_name`}
                      placeholder={PlaceHolderFormat(leaveTemplate.LEAVE_CATEGORY_NAME)}
                      value={leave.leave_category_name}
                      handleChange={leaveForm.handleChange}
                      handleBlur={leaveForm.handleBlur}
                      error={leaveForm.errors?.leaves?.[index]?.leave_category_name}
                      touched={leaveForm.touched?.leaves?.[index]?.leave_category_name}
                      disabled={isOther}
                      required={!isOther}
                    />
                    <BaseInput
                      type="text"
                      label={leaveTemplate.LEAVE_COUNT}
                      name={`leaves.${index}.leave_count`}
                      placeholder={PlaceHolderFormat(leaveTemplate.LEAVE_COUNT)}
                      value={leaveForm.values.leaves?.[index].leave_count}
                      handleChange={leaveForm.handleChange}
                      handleBlur={leaveForm.handleBlur}
                      error={leaveForm.errors?.leaves?.[index]?.leave_count}
                      touched={leaveForm.touched?.leaves?.[index]?.leave_count}
                      disabled={isOther}
                      required={!isOther}
                    />
                    <BaseInput
                      type="text"
                      label={leaveTemplate.CARRY_LIMIT}
                      name={`leaves.${index}.carry_forward_limit`}
                      placeholder={PlaceHolderFormat(leaveTemplate.CARRY_LIMIT)}
                      value={leaveForm.values.leaves?.[index].carry_forward_limit}
                      handleChange={leaveForm.handleChange}
                      handleBlur={leaveForm.handleBlur}
                      error={leaveForm.errors?.leaves?.[index]?.carry_forward_limit}
                      touched={leaveForm.touched?.leaves?.[index]?.carry_forward_limit}
                      disabled={isOther}
                      required={!isOther}
                    />
                    <div style={{ paddingTop: 30 }}>
                      {!isOther ? (
                        <button
                          type="button"
                          onClick={() => {
                            setLeaveIndexToDelete(index);
                            setIsModalOpen(true);
                          }}
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 9,
                            border: "1px solid var(--glass-border)",
                            background: "rgba(244,63,94,0.08)",
                            color: "#f43f5e",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <Trash2 size={14} strokeWidth={2} />
                        </button>
                      ) : (
                        <div style={{ width: 34 }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Save */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingBottom: 8 }}>
          {!isOnboarding && (
            <BaseButton color="cancel" type="button" onClick={() => navigate("/leaveTemplate")}>
              Cancel
            </BaseButton>
          )}
          <BaseButton color="primary" type="submit" loader={btnLoader} disabled={btnLoader}>
            {leaveTemplate.SAVE}
          </BaseButton>
        </div>
      </form>

      <BaseModal
        isOpen={isModalOpen}
        title={leaveTemplate.DELETE_CONFIRMATION}
        toggler={() => setIsModalOpen(false)}
        submit={confirmDelete}
        submitText={leaveTemplate.YES}
        loader={dltBtnLoader}
        disabled={dltBtnLoader}
      >
        <p style={{ margin: 0, color: "var(--text-primary)" }}>{leaveTemplate.DELETE_MESSAGE}</p>
      </BaseModal>
    </div>
  );
};

export default AddEditLeaveTemplate;
